/**
 * CSV Import Modal - Importera årshjulsuppgifter från CSV
 * 
 * Format: Titel;Beskrivning;Område;Frekvens;Startmånad;Förfallodatum;Taggar
 * Separator: ;
 * Stöd för multiline i beskrivningar med quotes
 * Taggar är valfri sista kolumn (komma-separerade)
 */

import React, { useState, useRef } from 'react';
import { X, Upload, AlertCircle, CheckCircle2, HelpCircle, Copy } from 'lucide-react';
import { PrepArea, TaskFrequency, TaskDeadline, deadlineLabels, useYearWheelStore } from '../stores/yearwheel-store';

interface ParsedTask {
  title: string;
  description: string;
  area: PrepArea;
  frequency: TaskFrequency;
  month: number;
  deadlineType: TaskDeadline;
  tags: string[];
}

interface ValidationError {
  line: number;
  field: string;
  message: string;
}

const VALID_AREAS: PrepArea[] = ['water', 'food', 'energy', 'health', 'information', 'shelter', 'transport', 'communication', 'safety', 'shopping', 'gardening', 'other'];
const VALID_FREQUENCIES: TaskFrequency[] = ['monthly', 'quarterly', 'biannual', 'annual'];

// Svenska översättningar för områden och frekvenser
const AREA_MAPPING: Record<string, PrepArea> = {
  'vatten': 'water',
  'mat': 'food',
  'energi': 'energy',
  'hälsa': 'health',
  'information': 'information',
  'skydd': 'shelter',
  'transport': 'transport',
  'kommunikation': 'communication',
  'säkerhet': 'safety',
  'inköp': 'shopping',
  'odla': 'gardening',
  'övrigt': 'other',
};

const FREQUENCY_MAPPING: Record<string, TaskFrequency> = {
  'månad': 'monthly',
  'kvartal': 'quarterly',
  'halvår': 'biannual',
  'år': 'annual',
  'årlig': 'annual',
  'årligen': 'annual',
  'månadsvis': 'monthly',
  'kvartalsvis': 'quarterly',
};

const MONTH_MAPPING: Record<string, number> = {
  'januari': 0, 'jan': 0,
  'februari': 1, 'feb': 1,
  'mars': 2, 'mar': 2,
  'april': 3, 'apr': 3,
  'maj': 4,
  'juni': 5, 'jun': 5,
  'juli': 6, 'jul': 6,
  'augusti': 7, 'aug': 7,
  'september': 8, 'sep': 8,
  'oktober': 9, 'okt': 9,
  'november': 10, 'nov': 10,
  'december': 11, 'dec': 11,
};

const DEADLINE_MAPPING: Record<string, TaskDeadline> = {
  'sista': 'lastDay',
  'sista dagen': 'lastDay',
  'lastday': 'lastDay',
  'last day': 'lastDay',
  '21': 'day21',
  '21a': 'day21',
  '21:a': 'day21',
  '21e': 'day21',
  '21:e': 'day21',
  '14': 'day14',
  '14e': 'day14',
  '14:e': 'day14',
  '7': 'day7',
  '7e': 'day7',
  '7:e': 'day7',
};

function parseDeadlineType(rawValue: string): TaskDeadline | null {
  const normalized = rawValue.toLowerCase().trim();

  if (!normalized) return null;

  if (DEADLINE_MAPPING[normalized]) {
    return DEADLINE_MAPPING[normalized];
  }

  const numeric = parseInt(normalized, 10);
  if (!isNaN(numeric)) {
    if (numeric === 7) return 'day7';
    if (numeric === 14) return 'day14';
    if (numeric === 21) return 'day21';
    if (numeric >= 28 && numeric <= 31) return 'lastDay';
  }

  return null;
}

interface CSVImportModalProps {
  onClose: () => void;
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({ onClose }) => {
  const [parsedTasks, setParsedTasks] = useState<ParsedTask[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [copiedExample, setCopiedExample] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addTask } = useYearWheelStore();

  const exampleCSV = `Titel;Beskrivning;Område;Frekvens;Startmånad;Förfallodatum;Taggar
Testa larm;Tryck på den röda knappen.;Säkerhet;Kvartal;Januari;7:e;säkerhet,brand
Rotera vatten;Byt ut vattnet i dunkarna.;Vatten;Halvår;Juni;14:e;vatten
Kontrollera brandsläckare;"Se till att brandsläckaren är laddad
och inom datum.";Säkerhet;År;Januari;Sista dagen;brandskydd,säkerhet
Kontrollera första hjälpen-väska;Fyll på det som saknas eller har gått ut.;Hälsa;Halvår;Januari;21:a;första hjälpen
Testa nödradio;Kontrollera att radion fungerar och hitta P4.;Information;År;Januari;7:e;kommunikation,information`;

  const handleCopyExample = async () => {
    try {
      await navigator.clipboard.writeText(exampleCSV);
      setCopiedExample(true);
      setTimeout(() => setCopiedExample(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Parse CSV-rad med stöd för quoted multiline
  const parseCSVLine = (text: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ';' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  // Validera och parsa CSV (stöd både 6 och 7 kolumner – Taggar är valfri sista kolumn)
  const parseCSV = (content: string): { tasks: ParsedTask[]; errors: ValidationError[] } => {
    const lines = content.split('\n').filter(line => line.trim());
    const tasks: ParsedTask[] = [];
    const errors: ValidationError[] = [];

    // Kontrollera maxgräns på 200 rader
    const maxRows = 200;
    const dataLines = lines.filter(line => !line.toLowerCase().includes('titel;beskrivning;område'));
    if (dataLines.length > maxRows) {
      errors.push({
        line: 0,
        field: 'CSV',
        message: `För många rader (${dataLines.length} st). Maxgräns är ${maxRows} uppgifter per import.`,
      });
      return { tasks, errors };
    }

    lines.forEach((line, index) => {
      const lineNumber = index + 1;
      
      // Skippa header-rad om den innehåller kolumnnamnen
      if (line.toLowerCase().includes('titel;beskrivning;område')) {
        return;
      }

      const fields = parseCSVLine(line);

      // Validera antal fält (6 utan taggar, 7 med taggar)
      if (fields.length < 6 || fields.length > 7) {
        errors.push({
          line: lineNumber,
          field: 'alla',
          message: `Fel antal kolumner (${fields.length} st, ska vara 6 eller 7)`,
        });
        return;
      }

      const [title, description, areaStr, frequencyStr, monthStr, deadlineStr, tagsStrRaw] = fields;

      // Validera titel
      if (!title || title.length === 0) {
        errors.push({ line: lineNumber, field: 'Titel', message: 'Titel saknas' });
        return;
      }
      if (title.length > 200) {
        errors.push({ line: lineNumber, field: 'Titel', message: 'Titel för lång (max 200 tecken)' });
        return;
      }

      // Validera beskrivning (valfri, men max 1000 tecken)
      if (description.length > 1000) {
        errors.push({ line: lineNumber, field: 'Beskrivning', message: 'Beskrivning för lång (max 1000 tecken)' });
        return;
      }

      // Validera område
      const normalizedArea = areaStr.toLowerCase().trim();
      const area = AREA_MAPPING[normalizedArea] || (VALID_AREAS.includes(normalizedArea as PrepArea) ? normalizedArea as PrepArea : null);
      
      if (!area) {
        errors.push({ 
          line: lineNumber, 
          field: 'Område', 
          message: `Ogiltigt område: "${areaStr}". Tillåtna: ${Object.keys(AREA_MAPPING).join(', ')}` 
        });
        return;
      }

      // Validera frekvens
      const normalizedFrequency = frequencyStr.toLowerCase().trim();
      const frequency = FREQUENCY_MAPPING[normalizedFrequency] || (VALID_FREQUENCIES.includes(normalizedFrequency as TaskFrequency) ? normalizedFrequency as TaskFrequency : null);
      
      if (!frequency) {
        errors.push({ 
          line: lineNumber, 
          field: 'Frekvens', 
          message: `Ogiltig frekvens: "${frequencyStr}". Tillåtna: ${Object.keys(FREQUENCY_MAPPING).join(', ')}` 
        });
        return;
      }

      // Validera månad
      const normalizedMonth = monthStr.toLowerCase().trim();
      let month: number | null = MONTH_MAPPING[normalizedMonth];
      
      // Om inte namn, försök parsa som nummer (0-11)
      if (month === undefined) {
        const monthNum = parseInt(monthStr, 10);
        if (!isNaN(monthNum) && monthNum >= 0 && monthNum <= 11) {
          month = monthNum;
        } else if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
          // Om 1-12, konvertera till 0-11
          month = monthNum - 1;
        }
      }
      
      if (month === null || month === undefined) {
        errors.push({ 
          line: lineNumber, 
          field: 'Startmånad', 
          message: `Ogiltig månad: "${monthStr}". Använd månadsnamn (Januari) eller nummer (1-12)` 
        });
        return;
      }

      // Validera förfallodatum. Accepterar både nya etiketter och äldre numeriska värden.
      const deadlineType = parseDeadlineType(deadlineStr);
      if (!deadlineType) {
        errors.push({ 
          line: lineNumber, 
          field: 'Förfallodatum', 
          message: `Ogiltigt värde: "${deadlineStr}". Använd Sista dagen, 21:a, 14:e eller 7:e` 
        });
        return;
      }

      // Validera/parsning av taggar (valfritt fält)
      const tags: string[] = tagsStrRaw
        ? tagsStrRaw
            .split(',')
            .map(t => t.trim())
            .filter(Boolean)
        : [];

      // Allt OK, lägg till uppgift
      tasks.push({
        title: title.replace(/^"|"$/g, ''), // Ta bort eventuella quotes
        description: description.replace(/^"|"$/g, ''),
        area,
        frequency,
        month,
        deadlineType,
        tags,
      });
    });

    return { tasks, errors };
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      
      const { tasks, errors } = parseCSV(content);
      setParsedTasks(tasks);
      setErrors(errors);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    if (parsedTasks.length === 0 || errors.length > 0) return;

    setIsProcessing(true);
    try {
      for (const task of parsedTasks) {
        await addTask({
          title: task.title,
          description: task.description,
          area: task.area,
          frequency: task.frequency,
          month: task.month,
          deadlineType: task.deadlineType,
          notifyDaysBefore: 7,
          tags: task.tags,
          enabled: true,
          isCustom: true,
        });
      }
      setImportSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Import error:', error);
      setErrors([{ line: 0, field: 'Import', message: 'Ett fel uppstod vid import' }]);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Upload className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Importera från CSV</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              title="Visa hjälp"
            >
              <HelpCircle className="h-5 w-5 text-muted-foreground" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Help section */}
          {showHelp && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-3 border border-border">
              <h3 className="font-semibold text-foreground">CSV-format</h3>
              <p className="text-sm text-muted-foreground">
                Filen ska vara i CSV-format med semikolon (;) som separator. Första raden kan vara en header (valfri).
              </p>
              <div className="bg-background rounded p-3 font-mono text-xs overflow-x-auto">
                <div className="text-muted-foreground mb-2">Format:</div>
                <div className="text-foreground">Titel;Beskrivning;Område;Frekvens;Startmånad;Förfallodatum;Taggar</div>
              </div>
              <div className="space-y-2 text-sm">
                <div><strong>Titel:</strong> Max 200 tecken</div>
                <div><strong>Beskrivning:</strong> Max 1000 tecken (använd "" för multiline)</div>
                <div><strong>Område:</strong> vatten, mat, energi, hälsa, information, skydd, transport, kommunikation, säkerhet, inköp, odla, övrigt</div>
                <div><strong>Frekvens:</strong> månad, kvartal, halvår, år</div>
                <div><strong>Startmånad:</strong> januari-december eller 1-12</div>
                <div><strong>Förfallodatum:</strong> Sista dagen, 21:a, 14:e eller 7:e</div>
                <div><strong>Taggar (valfritt):</strong> komma-separerade taggar, t.ex. <span className="font-mono">vatten,brand</span></div>
              </div>
              <div className="bg-background rounded p-3 font-mono text-xs overflow-x-auto space-y-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-muted-foreground">Exempel:</div>
                  <button
                    onClick={handleCopyExample}
                    className="flex items-center gap-1 px-2 py-1 rounded bg-muted hover:bg-muted/80 transition-colors text-foreground"
                  >
                    {copiedExample ? (
                      <>
                        <CheckCircle2 className="h-3 w-3" />
                        <span className="text-xs">Kopierat!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        <span className="text-xs">Kopiera exempel</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="text-foreground">Testa larm;Tryck på den röda knappen.;Säkerhet;Kvartal;Januari;7:e;säkerhet,brand</div>
                <div className="text-foreground">Rotera vatten;Byt ut vattnet i dunkarna.;Vatten;Halvår;Juni;14:e;vatten</div>
                <div className="text-foreground">Kontrollera brandsläckare;"Se till att brandsläckaren är laddad
och inom datum.";Säkerhet;År;Januari;Sista dagen;brandskydd,säkerhet</div>
                <div className="text-foreground">Kontrollera första hjälpen-väska;Fyll på det som saknas eller har gått ut.;Hälsa;Halvår;Januari;21:a;första hjälpen</div>
                <div className="text-foreground">Testa nödradio;Kontrollera att radion fungerar och hitta P4.;Information;År;Januari;7:e;kommunikation,information</div>
              </div>
            </div>
          )}

          {/* File upload */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full px-4 py-8 border-2 border-dashed border-border rounded-lg hover:border-primary hover:bg-muted/50 transition-colors flex flex-col items-center gap-3"
            >
              <Upload className="h-12 w-12 text-muted-foreground" />
              <div className="text-center">
                <div className="text-foreground font-medium">Välj CSV-fil</div>
                <div className="text-sm text-muted-foreground">Klicka för att bläddra</div>
              </div>
            </button>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-destructive font-semibold">
                <AlertCircle className="h-5 w-5" />
                <span>Valideringsfel ({errors.length} st)</span>
              </div>
              <div className="space-y-1 text-sm max-h-40 overflow-y-auto">
                {errors.map((error, idx) => (
                  <div key={idx} className="text-destructive">
                    {error.line > 0 ? `Rad ${error.line}` : 'Import'}, {error.field}: {error.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview */}
          {parsedTasks.length > 0 && errors.length === 0 && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-success font-semibold">
                <CheckCircle2 className="h-5 w-5" />
                <span>Redo att importera {parsedTasks.length} uppgift{parsedTasks.length !== 1 ? 'er' : ''}</span>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {parsedTasks.map((task, idx) => (
                  <div key={idx} className="bg-background rounded p-3 text-sm border border-border">
                    <div className="font-semibold text-foreground">{task.title}</div>
                    {task.description && (
                      <div className="text-muted-foreground text-xs mt-1">{task.description}</div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2 text-xs">
                      <span className="px-2 py-1 bg-muted rounded">{task.area}</span>
                      <span className="px-2 py-1 bg-muted rounded">{task.frequency}</span>
                      <span className="px-2 py-1 bg-muted rounded">Månad: {task.month + 1}</span>
                      <span className="px-2 py-1 bg-muted rounded">Förfallodatum: {deadlineLabels[task.deadlineType]}</span>
                      {task.tags.length > 0 && (
                        <span className="px-2 py-1 bg-muted rounded">Taggar: {task.tags.join(', ')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success message */}
          {importSuccess && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-success font-semibold">
                <CheckCircle2 className="h-5 w-5" />
                <span>Import slutförd!</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
            disabled={isProcessing}
          >
            Avbryt
          </button>
          <button
            onClick={handleImport}
            disabled={parsedTasks.length === 0 || errors.length > 0 || isProcessing || importSuccess}
            className="px-4 py-2 rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#67724e' }}
          >
            {isProcessing ? 'Importerar...' : `Importera ${parsedTasks.length} uppgift${parsedTasks.length !== 1 ? 'er' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSVImportModal;
